import { useCallback } from "react";
import { useAtomValue } from "jotai";
import { useTranslations } from "next-intl";
import {
  type ChatMessage,
  ChatMessageRole,
  ChatMessageType,
} from "@winging/util/type/chat.type";
import { useChatMessage } from "@/features/messageAI/model/ChatMessageContext";
import {
  isChatMessageBusySelector,
  lastChatMessageSelector,
} from "@/entities/chat";
import { useMessageInputCore } from "@/shared/lib/hooks/useMessageInputCore";
import type { UseMessageInputReturn } from "@/shared/types/messageInput.types";

/**
 * ChatMessageProvider 전용 hook (Demo / Builder)
 * DemoInput 등 Builder 컴포넌트에서 사용
 *
 * CloseChatRoom 이후 재전송 시 인사 메시지를 보내는 데모 전용 동작을 포함
 */
export const useBuilderMessageInput = (): UseMessageInputReturn => {
  const { sendQueryMessage, sendStaticMessage, resetChatRoom } =
    useChatMessage();
  const isBusy = useAtomValue(isChatMessageBusySelector);
  const lastChatMessage = useAtomValue(lastChatMessageSelector);
  const t = useTranslations("Landing");

  const wrappedSendQueryMessage = useCallback(
    async (message: Partial<ChatMessage>, lang: string) => {
      if (lastChatMessage?.type === ChatMessageType.CloseChatRoom) {
        await resetChatRoom();
        await sendStaticMessage({
          role: ChatMessageRole.STATIC_INFO,
          type: ChatMessageType.Default,
          status: "completed",
          text: t("demoInitText"),
        });
        return;
      }
      await sendQueryMessage(message, lang);
    },
    [lastChatMessage, resetChatRoom, sendStaticMessage, sendQueryMessage, t]
  );

  return useMessageInputCore({
    sendQueryMessage: wrappedSendQueryMessage,
    isBusy,
    isBuilder: true,
  });
};
